/**
 * FR-015-153, `contracts/screens.md` § Sign in, 001 FR-017: the one test a `redirect`
 * value passes before it is read anywhere. Exactly one leading `/` whose next character is
 * neither `/` nor `\`, which is the same test the API applies at
 * `GET /auth/oauth/{provider}/start`. An accepted path is passed through unchanged rather
 * than re-encoded. An absolute URL, a `//host`, a `/\host`, an empty value and any other
 * failing value are discarded silently: never echoed, never stored.
 */

/** The accepted path, or null for every value the test refuses. */
export function validateRedirect(value: string | null | undefined): string | null {
  if (value === null || value === undefined || value.length === 0) return null;
  if (!value.startsWith("/")) return null;
  const next = value.charAt(1);
  if (next === "/" || next === "\\") return null;
  // A control character or whitespace cannot be part of a route this client serves.
  for (const char of value) {
    const code = char.codePointAt(0) ?? 0;
    if (code < 0x20 || code === 0x7f || /\s/.test(char)) return null;
  }
  return value;
}

/** The last route of the last workspace, as the two storage keys hold them. */
export interface LastVisit {
  readonly workspace: string | null;
  /** The route stored for that workspace, an instance-relative path. */
  readonly route: string | null;
}

/**
 * Where a completed sign-in lands: the validated `redirect`, else the last route of the
 * last workspace, else that workspace's root, else `/`.
 */
export function resolveDestination(redirect: string | null | undefined, last: LastVisit): string {
  const accepted = validateRedirect(redirect);
  if (accepted !== null) return accepted;
  const workspace = last.workspace ?? "";
  if (workspace.length > 0) {
    const route = validateRedirect(last.route);
    if (route?.startsWith(`/${workspace}`) === true) return route;
    return `/${workspace}`;
  }
  return "/";
}
